<template>
  <div class="url-upload-component">
    <!-- WebP 配置面板 -->
    <div class="webp-config-panel" v-if="showWebpConfig">
      <el-row :gutter="10">
        <el-col :span="8">
          <div class="config-item">
            <span class="config-label">WebP转换:</span>
            <el-switch
              v-model="internalConvertToWebP"
              active-text="启用"
              inactive-text="禁用"
              size="small"
            ></el-switch>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="config-item">
            <span class="config-label">质量:</span>
            <el-select
              v-model="internalWebpQuality"
              :disabled="!internalConvertToWebP"
              size="small"
              placeholder="选择质量"
              style="width: 100%"
            >
              <el-option label="高质量" value="high"></el-option>
              <el-option label="中等质量(推荐)" value="medium"></el-option>
              <el-option label="低质量" value="low"></el-option>
              <el-option label="最小体积" value="smallest"></el-option>
              <el-option label="极限压缩" value="extreme"></el-option>
            </el-select>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="config-item">
            <span class="config-label">存储桶:</span>
            <el-input
              v-model="internalBucket"
              size="small"
              placeholder="bucket名称"
              :disabled="!!bucket"
            ></el-input>
          </div>
        </el-col>
      </el-row>
      <div class="config-tip">
        <i class="el-icon-info"></i>
        {{ getConfigTip() }}
      </div>
    </div>

    <!-- URL 输入和上传区域 -->
    <div class="upload-area">
      <el-input
        v-model="imageUrl"
        placeholder="请输入图片URL地址（支持http/https）"
        :disabled="uploading"
        @keyup.enter.native="handleUpload"
      >
        <el-button
          slot="append"
          icon="el-icon-upload"
          :loading="uploading"
          @click="handleUpload"
        >
          {{ uploading ? '上传中...' : '上传到MinIO' }}
        </el-button>
      </el-input>

      <!-- 上传进度 -->
      <el-progress
        v-if="showProgress && uploading"
        :percentage="uploadProgress"
        :status="uploadStatus"
        :stroke-width="6"
        style="margin-top: 10px;"
      ></el-progress>

      <!-- 上传结果 -->
      <div v-if="uploadResult" class="upload-result">
        <el-alert
          :title="uploadResult.success ? '上传成功' : '上传失败'"
          :type="uploadResult.success ? 'success' : 'error'"
          :closable="false"
          show-icon
        >
          <!-- <div v-if="uploadResult.success">
            <div class="result-label">MinIO URL:</div>
            <div class="result-url">{{ uploadResult.url }}</div>
            <el-button
              size="mini"
              type="primary"
              icon="el-icon-document-copy"
              @click="copyUrl(uploadResult.url)"
              style="margin-top: 5px;"
            >
              复制链接
            </el-button>
          </div> -->
          <div v-if="!uploadResult.success">
            <div>错误信息: {{ uploadResult.error }}</div>
          </div>
        </el-alert>
      </div>
    </div>

    <!-- 使用提示 -->
    <div class="upload-tip" v-if="showTip">
      <i class="el-icon-info"></i>
      <span>
        支持输入外部图片URL，将自动上传到MinIO存储。
        支持格式: JPEG/PNG/GIF/WebP/BMP/TIFF等，单文件最大50MB
      </span>
    </div>
  </div>
</template>

<script>
import { getMinioBucket } from '@/api/system/config'

export default {
  name: 'UrlUpload',
  props: {
    // 是否转换为WebP
    convertToWebP: {
      type: Boolean,
      default: true
    },
    // WebP质量: high/medium/low/smallest/extreme
    webpQuality: {
      type: String,
      default: 'smallest',
      validator: (value) => ['high', 'medium', 'low', 'smallest', 'extreme'].includes(value)
    },
    // 存储桶名称
    bucket: {
      type: String,
      default: ''
    },
    // 是否显示上传进度
    showProgress: {
      type: Boolean,
      default: true
    },
    // 是否显示 WebP 配置面板
    showWebpConfig: {
      type: Boolean,
      default: true
    },
    // 是否显示提示
    showTip: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      imageUrl: '',
      uploading: false,
      uploadProgress: 0,
      uploadStatus: '',
      uploadResult: null,
      // 内部配置状态
      internalConvertToWebP: this.convertToWebP,
      internalWebpQuality: this.webpQuality,
      internalBucket: this.bucket,
      // API地址
      uploadApiUrl: 'https://test-minio.asdf.live/api/external/upload-from-url'
    }
  },
  mounted() {
    // 如果没有传入 bucket，则从接口获取
    if (!this.bucket) {
      this.fetchBucket()
    }
  },
  methods: {
    // 获取 bucket 配置
    async fetchBucket() {
      try {
        const response = await getMinioBucket()
        if (response && response.data) {
          this.internalBucket = response.data
        }
      } catch (error) {
        console.error('获取 bucket 配置失败:', error)
        this.internalBucket = 'kanbei' // 默认值
      }
    },
    // 获取配置提示
    getConfigTip() {
      const qualityLabels = {
        high: '高质量',
        medium: '中等质量',
        low: '低质量',
        smallest: '最小体积',
        extreme: '极限压缩'
      }
      const bucket = this.internalBucket || '默认'
      if (this.internalConvertToWebP) {
        const quality = qualityLabels[this.internalWebpQuality] || this.internalWebpQuality
        return `存储桶: ${bucket} | WebP转换: 启用 (${quality})`
      } else {
        return `存储桶: ${bucket} | WebP转换: 禁用（保持原格式）`
      }
    },
    // 处理上传
    async handleUpload() {
      if (!this.imageUrl || !this.imageUrl.trim()) {
        this.$message.warning('请输入图片URL地址')
        return
      }

      // 验证URL格式
      if (!this.isValidUrl(this.imageUrl)) {
        this.$message.error('请输入有效的HTTP或HTTPS链接')
        return
      }

      this.uploading = true
      this.uploadProgress = 0
      this.uploadStatus = ''
      this.uploadResult = null

      try {
        // 构建请求参数
        const requestData = {
          imageUrls: [this.imageUrl.trim()]
        }

        // 添加 WebP 配置
        const useConvert = this.showWebpConfig ? this.internalConvertToWebP : this.convertToWebP
        const useQuality = this.showWebpConfig ? this.internalWebpQuality : this.webpQuality
        const useBucket = this.showWebpConfig ? this.internalBucket : (this.bucket || this.internalBucket)

        if (useConvert) {
          requestData.convertToWebP = true
          requestData.webpQuality = useQuality
        }
        if (useBucket) {
          requestData.bucket = useBucket
        }

        // 模拟进度
        this.simulateProgress()

        // 发送请求
        const response = await this.$http.post(this.uploadApiUrl, requestData)

        this.uploadProgress = 100
        this.uploadStatus = 'success'

        // 处理响应
        if (response.data.success) {
          const uploadedUrl = response.data.data.url
          this.uploadResult = {
            success: true,
            url: uploadedUrl
          }

          // 触发事件，通知父组件
          this.$emit('success', uploadedUrl)
          this.$message.success('上传成功！')
        } else {
          throw new Error(response.data.error?.message || '上传失败')
        }
      } catch (error) {
        this.uploadProgress = 100
        this.uploadStatus = 'exception'
        this.uploadResult = {
          success: false,
          error: error.response?.data?.error?.message || error.message || '上传失败，请检查URL是否可访问'
        }
        this.$message.error(this.uploadResult.error)
        this.$emit('error', this.uploadResult.error)
      } finally {
        this.uploading = false
      }
    },
    // 模拟上传进度
    simulateProgress() {
      let progress = 0
      const interval = setInterval(() => {
        if (!this.uploading || progress >= 90) {
          clearInterval(interval)
          return
        }
        progress += Math.random() * 30
        if (progress > 90) progress = 90
        this.uploadProgress = Math.round(progress)
      }, 300)
    },
    // 验证URL
    isValidUrl(url) {
      try {
        const urlObj = new URL(url)
        return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
      } catch (e) {
        return false
      }
    },
    // 复制URL
    copyUrl(url) {
      const textarea = document.createElement('textarea')
      textarea.value = url
      document.body.appendChild(textarea)
      textarea.select()
      try {
        document.execCommand('copy')
        this.$message.success('链接已复制到剪贴板')
      } catch (err) {
        this.$message.error('复制失败，请手动复制')
      }
      document.body.removeChild(textarea)
    },
    // 重置
    reset() {
      this.imageUrl = ''
      this.uploadResult = null
      this.uploadProgress = 0
      this.uploadStatus = ''
    }
  },
  watch: {
    convertToWebP(val) {
      this.internalConvertToWebP = val
    },
    webpQuality(val) {
      this.internalWebpQuality = val
    },
    bucket(val) {
      if (val) {
        this.internalBucket = val
      }
    }
  }
}
</script>

<style scoped lang="scss">
.url-upload-component {
  width: 100%;
}

// WebP 配置面板
.webp-config-panel {
  background: #f5f7fa;
  padding: 12px 15px;
  border-radius: 4px;
  margin-bottom: 15px;
  border: 1px solid #e4e7ed;

  .config-item {
    display: flex;
    align-items: center;
    gap: 8px;

    .config-label {
      font-size: 13px;
      color: #606266;
      font-weight: 500;
      min-width: 70px;
      white-space: nowrap;
    }
  }

  .config-tip {
    margin-top: 8px;
    font-size: 12px;
    color: #909399;
    display: flex;
    align-items: center;
    gap: 5px;

    i {
      font-size: 14px;
    }
  }
}

// 上传区域
.upload-area {
  margin-bottom: 10px;
}

// 上传结果
.upload-result {
  margin-top: 15px;

  .result-label {
    font-size: 12px;
    color: #909399;
    margin-bottom: 5px;
  }

  .result-url {
    font-size: 13px;
    color: #303133;
    word-break: break-all;
    padding: 8px;
    background: #f4f4f5;
    border-radius: 4px;
    margin-bottom: 5px;
  }
}

// 使用提示
.upload-tip {
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin-top: 8px;
  line-height: 1.5;

  i {
    font-size: 14px;
    margin-top: 2px;
    flex-shrink: 0;
  }
}
</style>
